import { action, flow } from 'mobx';
import { codeSplit, Controller, FetchOptions, FetchPageOptions, StoreContainer } from 'bernie-core';
import { AnalyticsStore, ContextStore } from 'bernie-plugin-mobx';
import { PageData } from 'bernie-http';
import { SystemEvent, SystemEventLevel } from 'bernie-logger';
import { ClientLogger } from 'bernie-client';
import { getBexApiContext } from 'bernie-context';

import { ClientMetadataStore, ErrorStore, FlightServicingStore, ServicingCriteriaStore } from 'stores';
import { PageId, getPageName, generateRequestOptions } from 'utility';
import { getNavigationIdFromQuery } from 'utility/navigation-identity';
import { FlightsShoppingPwaFlightServicingResults } from 'src/__generated__/api';

export class FlightServicingController implements Controller {
  public pageId = PageId.FLIGHT_SERVICING;
  public path = '*Flight-Servicing';
  public routeName = 'flight-servicing';
  public bundles = [];
  public exact = true;
  /* istanbul ignore next */
  public component = codeSplit(() => import(/* webpackChunkName: "servicing" */ '../views/flight-servicing'));
  public pendingPromises = [];
  private logger = ClientLogger.getLoggerWithIdentifier('controller.flightServicing');

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public async fetch(options: FetchOptions): Promise<any> {
    if (!options.isServer) {
      return this.fetchClientSide(options);
    }

    const { servicingCriteriaStore } = this.getFetchVariables(options.stores);
    servicingCriteriaStore.setCriteriaFromUrl(options);

    return Promise.resolve({});
  }

  public fetchPageData(pageData: FetchPageOptions): Promise<PageData> {
    // To-Do: Localize page title
    const title = 'Flight Servicing';
    const query = pageData.query;
    const pageName = getPageName(this.pageId);
    const navigationId = getNavigationIdFromQuery(query);

    return Promise.resolve({ title, pageId: this.pageId, pageName, navigationId });
  }

  private fetchClientSide(options: FetchOptions) {
    if (this.pendingPromises.length > 0) {
      this.pendingPromises.forEach(p => p.cancel());
    }

    const clientPromise = this.createFlowForClientFetch(options, this.pageId);

    this.pendingPromises.push(clientPromise);

    return clientPromise;
  }

  @action
  private createFlowForClientFetch = flow(function* (options: FetchOptions, pageId: string) {
    const {
      analyticsStore,
      clientMetadataStore,
      errorStore,
      flightServicingStore,
      servicingCriteriaStore,
      variables
    } = this.getFetchVariables(options.stores);

    const requestOptions = generateRequestOptions(options.request, options.isServer, pageId);
    servicingCriteriaStore.setCriteriaFromUrl(options);
    errorStore.clientFetchFailure = ErrorStore.DEFAULTS.clientFetchFailure;

    try {
      const response: FlightsShoppingPwaFlightServicingResults.FlightsServicing = yield flightServicingStore.fetchResults(
        variables,
        requestOptions,
        analyticsStore
      );

      if (!response || Object.keys(response).length == 0) {
        errorStore.setClientFetchFailure(true);
        this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'SERVICING_FETCH_FAILED'));

        return;
      }
      const { clientMetadata, serverErrorMessaging } = response;

      clientMetadataStore.hydrate({ clientMetadata });
      errorStore.hydrate({ failedServicingMessaging: serverErrorMessaging });

      serverErrorMessaging?.placards && errorStore.setPriceChangeError(true);

      this.pendingPromises = [];

      return;
    } catch (error) {
      errorStore.setClientFetchFailure(true);
      this.logger.logEvent(new SystemEvent(SystemEventLevel.CRITICAL, 'SERVICING_CLIENT_HYDRATION'), error);
    }
  });

  private getFetchVariables(stores: StoreContainer) {
    const analyticsStore = stores.get<AnalyticsStore>('analytics');
    const bernieContext: ContextStore = stores.get<ContextStore>('context');
    const clientMetadataStore = stores.get<ClientMetadataStore>('clientMetadataStore');
    const context = getBexApiContext(bernieContext);
    const errorStore = stores.get<ErrorStore>('errorStore');
    const flightServicingStore = stores.get<FlightServicingStore>('flightServicingStore');
    const servicingCriteriaStore = stores.get<ServicingCriteriaStore>('servicingCriteriaStore');
    const variables = { context, ...servicingCriteriaStore.servicingCriteria };

    return {
      analyticsStore,
      clientMetadataStore,
      errorStore,
      flightServicingStore,
      servicingCriteriaStore,
      variables
    };
  }
}
